/**
 * Unit 1.4 · IMPL.E: designed empty/error states (never default shells).
 *
 * Design contract: docs/design-briefs/2026-06-03-clients-list.md.
 *
 * `StateShell` is the feature's one icon-circle + headline recipe; the
 * conversations tab and ClientDetail's not-found/error states render
 * through it too, so the chrome can't drift between surfaces.
 */
import { AlertCircle, Search, UserPlus, Users } from "lucide-react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface StateShellProps extends ComponentPropsWithoutRef<"div"> {
    icon: ReactNode;
    headline: string;
}

export function StateShell({
    icon,
    headline,
    className,
    children,
    ...rest
}: StateShellProps) {
    return (
        <div
            className={cn(
                "flex flex-col items-center gap-3 py-12 text-center",
                className
            )}
            {...rest}
        >
            <span className="bg-muted flex size-12 items-center justify-center rounded-full">
                {icon}
            </span>
            <p className="text-foreground text-sm font-medium">{headline}</p>
            {children}
        </div>
    );
}

export function ClientListEmptyLibrary({
    onAddClient,
}: {
    onAddClient: () => void;
}) {
    return (
        <StateShell
            icon={<Users className="text-muted-foreground size-5" />}
            headline="No clients yet"
        >
            <p className="text-muted-foreground max-w-sm text-sm">
                Add your first client to start tagging documents and tracking
                activity.
            </p>
            <Button onClick={onAddClient} className="mt-1">
                <UserPlus className="size-4" />
                Add Client
            </Button>
        </StateShell>
    );
}

export function ClientListEmptySearch({
    query,
    onClearSearch,
}: {
    query: string;
    onClearSearch: () => void;
}) {
    return (
        <StateShell
            icon={<Search className="text-muted-foreground size-5" />}
            headline={`No clients match "${query}".`}
        >
            <Button variant="link" onClick={onClearSearch}>
                Clear search
            </Button>
        </StateShell>
    );
}

export function ClientListError({ onRetry }: { onRetry: () => void }) {
    return (
        <StateShell
            icon={<AlertCircle className="text-muted-foreground size-5" />}
            headline="Something went wrong loading clients"
        >
            <Button variant="ghost" onClick={onRetry}>
                Try again
            </Button>
        </StateShell>
    );
}
